/**
 * Who can sign in for this business. The owner adds a person by name, email
 * and mobile and shares the invitation link; the person sets their password.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { Badge, Card, ErrorNote, Field, Spinner, SubHeader } from '../components/Primitives';

interface Login {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  joined: boolean;
  lastLoginAt: string | null;
}

export function TeamPage() {
  const T = useT();
  const [logins, setLogins] = useState<Login[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ fullName: '', email: '', phone: '' });
  const [link, setLink] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () =>
    api.get<{ logins: Login[] }>('/v1/partner/team').then(
      (r) => setLogins(r.logins),
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);
  if (error) return <ErrorNote message={error} />;
  if (!logins) return <Spinner />;

  const invite = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ link: string }>('/v1/partner/team', form);
      setLink(r.link);
      setForm({ fullName: '', email: '', phone: '' });
      void load();
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    if (!confirm(T('इनका लॉगिन हटाएँ?', 'Remove this login?'))) return;
    await api.post(`/v1/partner/team/${id}/remove`).catch(() => undefined);
    void load();
  };

  return (
    <div className="space-y-4">
      <SubHeader
        icon="team"
        title={T('टीम लॉगिन', 'Team logins')}
        sub={T(
          'हर किसी का अपना लॉगिन — कौन क्या कर सकता है, आप तय करें।',
          'Everyone gets their own login — you decide who can do what.',
        )}
      />
      <ul className="space-y-2">
        {logins.map((l) => (
          <li key={l.id}>
            <Card>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">{l.name}</p>
                  <p className="truncate text-xs text-muted">
                    {l.email} · {l.phone}
                  </p>
                </div>
                <Badge tone={l.joined ? 'good' : 'warn'}>
                  {l.role === 'owner'
                    ? T('मालिक', 'Owner')
                    : l.joined
                      ? T('जुड़ गए', 'Joined')
                      : T('न्योता भेजा', 'Invited')}
                </Badge>
              </div>
              {l.role !== 'owner' && (
                <button className="btn-quiet mt-2 text-sm" onClick={() => void remove(l.id)}>
                  {T('हटाएँ', 'Remove')}
                </button>
              )}
            </Card>
          </li>
        ))}
      </ul>
      <Card>
        <p className="mb-2 font-semibold">{T('किसी को जोड़ें', 'Add someone')}</p>
        <div className="space-y-3">
          <Field label={T('नाम', 'Name')}>
            <input
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </Field>
          <Field label={T('ईमेल', 'Email')}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label={T('मोबाइल', 'Mobile')}>
            <input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <button
            className="btn-primary"
            disabled={busy || !form.fullName || !form.email || !form.phone}
            onClick={() => void invite()}
          >
            {T('न्योता लिंक बनाएँ', 'Make an invitation link')}
          </button>
          {msg && <p className="text-sm text-offline">{msg}</p>}
          {link && (
            <CopyLink
              link={link}
              note={T(
                'यह लिंक उन्हें WhatsApp पर भेजें। 14 दिन चलेगा।',
                'Send this link to them on WhatsApp. It works for 14 days.',
              )}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export function CopyLink({ link, note }: { link: string; note: string }) {
  const T = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl bg-panel-2 p-3">
      <p className="text-xs text-muted">{note}</p>
      <p className="mt-1 break-all font-mono text-xs">{link}</p>
      <button
        className="btn-secondary mt-2"
        onClick={() => void navigator.clipboard?.writeText(link).then(() => setCopied(true))}
      >
        {copied ? T('कॉपी हो गया ✓', 'Copied ✓') : T('कॉपी करें', 'Copy')}
      </button>
    </div>
  );
}
